import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  cartAllProductsSelector,
  cartAppliedCouponsSelector,
  cartEmailSelector,
  cartIdSelector,
  cartPricesSelector,
  cartTaxesTotalAmountSelector,
  checkoutSelectedShippingMethodSelector,
  checkoutShippingAddressesSelector,
  resolveCatchMessage,
  TCatchMessage,
  useAffirmConfigQuery,
  useInitAffirmConfigMutation,
  useLoadingContext,
  usePlaceOrderMutation,
  useSetPaymentMethodOnCartMutation,
  useToastContext,
} from '@segway/core'
import { AffirmDataParsed } from '@segway/core/src/businessHooks/useProductAffirmData'
import { useAppSelector } from '@segway/core/src/store/hooks'

import { generateGTMCartItemsParams } from '@/utils'
import reportGTMEvent from '@/utils/gtm'

/**
 * 结算页Affirm Hooks
 */
const useCheckoutAffirm = ({
  totalPrice,
  router,
  googleReCaptchaToken,
}: {
  totalPrice: number
  affirmData: AffirmDataParsed | null
  router: {
    replace: (url: string) => void
  }
  googleReCaptchaToken: string
}) => {
  const toast = useToastContext()
  const loading = useLoadingContext()
  const shippingAddresses = useAppSelector(checkoutShippingAddressesSelector)
  const cartAllProducts = useAppSelector(cartAllProductsSelector)
  const cartEmail = useAppSelector(cartEmailSelector)
  const cartId = useAppSelector(cartIdSelector)
  const cartPrices = useAppSelector(cartPricesSelector)
  const appliedCoupons = useAppSelector(cartAppliedCouponsSelector)
  const taxesTotalAmount = useAppSelector(cartTaxesTotalAmountSelector)
  const selectedShippingMethod = useAppSelector(checkoutSelectedShippingMethodSelector)

  const { data: affirmConfigQueryData } = useAffirmConfigQuery({})
  const [initAffirmConfigMutation] = useInitAffirmConfigMutation()
  const [setPaymentMethodOnCartMutation] = useSetPaymentMethodOnCartMutation()
  const [placeOrderMutation] = usePlaceOrderMutation()

  const [isScriptLoad, setIsScriptLoad] = useState(false)

  const shippingAddress = useMemo(() => {
    return shippingAddresses?.[0] ?? null
  }, [shippingAddresses])

  const affirmConfig = useMemo(() => {
    try {
      if (affirmConfigQueryData?.affirmConfig) {
        return JSON.parse(affirmConfigQueryData.affirmConfig)
      }
      return null
    } catch (e) {
      toast.show({
        icon: 'fail',
        content: resolveCatchMessage(e as TCatchMessage),
      })
      return null
    }
  }, [affirmConfigQueryData, toast])

  const isReady = useMemo(() => {
    return isScriptLoad && affirmConfig
  }, [affirmConfig, isScriptLoad])

  /**
   * 支付成功回调, 下单
   */
  const handleOnSuccess = useCallback(
    async (checkoutOption: { checkout_token: string }) => {
      try {
        if (checkoutOption?.checkout_token) {
          reportGTMEvent({
            event: 'add_payment_info',
            ecommerce: {
              currency: cartPrices?.grand_total?.currency,
              value: cartPrices?.subtotal_excluding_tax?.value,
              payment_type: 'affirm_gateway',
              coupon: appliedCoupons && appliedCoupons?.length ? appliedCoupons[0]?.code : '',
              items: generateGTMCartItemsParams(cartAllProducts),
            },
          })

          // 设置支付方式
          const res = await setPaymentMethodOnCartMutation({
            input: {
              cart_id: cartId,
              payment_method: {
                code: 'affirm_gateway',
                affirm: {
                  checkout_token: checkoutOption.checkout_token,
                },
              },
            },
          })
          if (res?.error) {
            toast.show({
              icon: 'fail',
              content: resolveCatchMessage(res.error as TCatchMessage),
            })
            loading.hide()
            return
          }
          if (res?.data?.setPaymentMethodOnCart) {
            // 下单
            const headers = googleReCaptchaToken
              ? {
                  headers: {
                    'X-ReCaptcha': googleReCaptchaToken,
                  },
                }
              : {}
            const placeOrderRes = await placeOrderMutation({
              variables: {
                input: {
                  cart_id: cartId,
                },
              },
              ...headers,
            })
            if (placeOrderRes?.error) {
              toast.show({
                icon: 'fail',
                content: resolveCatchMessage(placeOrderRes.error as TCatchMessage),
              })
              loading.hide()
              return
            }
            if (placeOrderRes?.data?.placeOrder?.order?.order_id) {
              /**
               * GTM: purchase
               */
              reportGTMEvent({
                event: 'purchase',
                ecommerce: {
                  transaction_id: placeOrderRes.data.placeOrder.order.order_id,
                  currency: cartPrices?.grand_total?.currency,
                  value: cartPrices?.grand_total?.value,
                  tax: taxesTotalAmount,
                  shipping: selectedShippingMethod?.amount.value,
                  coupon: appliedCoupons && appliedCoupons.length ? appliedCoupons[0]?.code : '',
                  items: generateGTMCartItemsParams(cartAllProducts),
                },
              })

              router.replace(`/checkout/success/${placeOrderRes.data.placeOrder.order.order_id}`)
            } else {
              toast.show({
                icon: 'fail',
                content: 'Place order failed',
              })
            }
          } else {
            toast.show({
              icon: 'fail',
              content: 'Set payment method on cart failed',
            })
          }
        } else {
          toast.show({
            icon: 'fail',
            content: 'There is no token here',
          })
        }
      } catch (e) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(e as TCatchMessage),
        })
      } finally {
        loading.hide()
      }
    },
    [
      cartId,
      loading,
      placeOrderMutation,
      router,
      setPaymentMethodOnCartMutation,
      toast,
      googleReCaptchaToken,
      cartAllProducts,
      cartPrices,
      appliedCoupons,
      taxesTotalAmount,
      selectedShippingMethod,
    ],
  )

  /**
   * 点击Affirm支付
   */
  const handleinitAffirmConfigMutation = useCallback(async () => {
    try {
      await initAffirmConfigMutation({
        cart_id: cartId,
      })
    } catch (e) {
      toast.show({
        icon: 'fail',
        content: resolveCatchMessage(e as TCatchMessage),
      })
    }
  }, [cartId, initAffirmConfigMutation, toast])

  const launchAffirm = useCallback(() => {
    try {
      if (!window.affirm) {
        toast.show({
          icon: 'fail',
          content: 'Affirm SDK not loaded',
        })
        return
      }
      if (!shippingAddress) {
        toast.show({
          icon: 'fail',
          content: 'Required shipping information is missing',
        })
        return
      }
      if (!totalPrice) {
        toast.show({
          icon: 'fail',
          content: 'Required total price is missing',
        })
        return
      }
      if (cartAllProducts?.length === 0) {
        toast.show({
          icon: 'fail',
          content: 'Required products is missing',
        })
        return
      }
      if (!cartEmail) {
        toast.show({
          icon: 'fail',
          content: 'Required cart email is missing',
        })
        return
      }
      loading.show()

      const checkoutQuery = {
        merchant: {
          public_api_key: affirmConfig.apiKeyPublic,
          user_confirmation_url: 'https://segway-us.mez100.com.cn/checkout', // TODO
          user_cancel_url: 'https://segway-us.mez100.com.cn/checkout', // TODO
          // user_confirmation_url_action: 'POST',
          // name: 'Your Customer-Facing Merchant Name',
        },
        shipping: {
          name: {
            first: shippingAddress?.firstname ?? '',
            last: shippingAddress?.lastname ?? '',
          },
          address: {
            line1: shippingAddress?.street?.[0] ?? '',
            line2: shippingAddress?.street?.[0] ?? '',
            city: shippingAddress?.city ?? '',
            state: shippingAddress?.region?.code ?? '',
            zipcode: shippingAddress?.postcode ?? '',
            country: shippingAddress?.country?.code ?? '',
            // country: 'USA',
          },
          phone_number: shippingAddress?.telephone ?? '',
          email: cartEmail,
        },
        items: cartAllProducts.map((item) => {
          const priceRange = item?.rawProduct?.product?.price_range ?? null
          return {
            display_name: item?.rawProduct?.product?.name ?? '',
            sku: item?.rawProduct?.product?.sku ?? '',
            // TODO: 产品单价
            unit_price:
              (priceRange?.minimum_price?.discount?.amount_off ?? 0) > 0
                ? (priceRange?.minimum_price?.final_price?.value ?? 0) * 100 // 转为美分
                : (priceRange?.minimum_price?.regular_price?.value ?? 0) * 100, // 转为美分
            qty: item?.quantity ?? 0,
          }
        }),
        // discounts: {
        //   RETURN5: {
        //     discount_amount: 500,
        //     discount_display_name: 'Returning customer 5% discount',
        //   },
        //   PRESDAY10: {
        //     discount_amount: 1000,
        //     discount_display_name: "President's Day 10% off",
        //   },
        // },
        metadata: {
          // shipping_type: 'UPS Ground',
          mode: 'modal',
        },
        // order_id: 'JKLMO4321',
        currency: 'USD',
        // financing_program: 'flyus_3z6r12r',
        // shipping_amount: 1000,
        // tax_amount: 500,
        total: totalPrice * 100, // 转为美分,
      }
      window.affirm.checkout(checkoutQuery)
      window.affirm?.checkout?.open({
        onFail: (error) => {
          loading.hide()
          console.log('onFail: ', error)
        },
        onSuccess: (checkout) => {
          console.log('onSuccess: ', checkout)
          handleOnSuccess(checkout as { checkout_token: string })
        },
        onOpen: (token) => {
          console.log('onOpen: ', token)
          loading.hide()
        },
        onValidationError: (checkout_validation_error) => {
          loading.hide()
          console.log('onValidationError: ', checkout_validation_error)
        },
      })
    } catch (e) {
      loading.hide()
      toast.show({
        icon: 'fail',
        content: resolveCatchMessage(e as TCatchMessage),
      })
    }
  }, [
    affirmConfig,
    cartAllProducts,
    totalPrice,
    loading,
    shippingAddress,
    toast,
    cartEmail,
    handleOnSuccess,
  ])

  useEffect(() => {
    if (affirmConfig && !window._affirm_config) {
      window._affirm_config = {
        public_api_key: affirmConfig.apiKeyPublic,
        script: affirmConfig.script,
        country_code: affirmConfig.countryCode,
        locale: affirmConfig.locale,
      }
    }
  }, [affirmConfig])

  useEffect(() => {
    // TODO
    // handleinitAffirmConfigMutation()
  }, [handleinitAffirmConfigMutation])

  return { affirmConfig, launchAffirm, setIsScriptLoad, isReady }
}

export default useCheckoutAffirm
